import React, { useState, useEffect } from 'react';
import { liveBlogAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Radio, Plus, Eye, Trash2, Send, Clock, Pin, PinOff, Image as ImageIcon, FileText } from 'lucide-react';
import { liveBlogExtrasAPI } from '../../lib/api';
import { toast } from 'sonner';

export default function AdminLiveBlog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [form, setForm] = useState({ title: '', description: '' });
  const [updateText, setUpdateText] = useState('');

  useEffect(() => { loadBlogs(); }, []);

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const res = await liveBlogAPI.list({ active_only: false });
      setBlogs(res.data.live_blogs || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const createBlog = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await liveBlogAPI.create(form);
      toast.success('Live blog created');
      setShowCreate(false); setForm({ title: '', description: '' });
      loadBlogs();
    } catch { toast.error('Failed'); }
  };

  const openDetail = async (id) => {
    setShowDetail(id);
    try {
      const res = await liveBlogAPI.get(id);
      setDetailData(res.data);
    } catch { toast.error('Failed to load'); }
  };

  const [updateType, setUpdateType] = useState('text');
  const [updateMedia, setUpdateMedia] = useState('');

  const postUpdate = async () => {
    if (!updateText.trim()) return;
    try {
      const payload = { content: updateText, update_type: updateType };
      if (updateMedia) payload.metadata = { media_url: updateMedia, media_type: updateType };
      await liveBlogAPI.postUpdate(showDetail, payload);
      setUpdateText(''); setUpdateMedia('');
      openDetail(showDetail);
      toast.success('Update posted');
    } catch { toast.error('Failed'); }
  };

  const togglePin = async (blogId, updateId, isPinned) => {
    try {
      await liveBlogAPI.postUpdate(blogId, { update_id: updateId, is_pinned: !isPinned });
      openDetail(blogId);
      toast.success(isPinned ? 'Unpinned' : '📌 Pinned as key moment');
    } catch { toast.error('Failed'); }
  };

  const summarizeBlog = async (blogId) => {
    try {
      const r = await liveBlogExtrasAPI.summarize(blogId);
      toast.success('Summary generated');
      alert(`Summary:\n\n${r.data.summary}`);
    } catch { toast.error('Failed to summarize'); }
  };

  const convertToArticle = async (blogId) => {
    try {
      const r = await liveBlogExtrasAPI.convertToArticle(blogId, { title: detailData?.title });
      toast.success(`Converted to article draft (ID: ${r.data.article_id})`);
      setShowDetail(null);
    } catch { toast.error('Failed to convert'); }
  };

  const deleteBlog = async (id) => {
    try { await liveBlogAPI.delete(id); toast.success('Deleted'); setShowDetail(null); loadBlogs(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-live-blog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Radio className="w-6 h-6 text-[#B3261E]" /> Live Blog</h1>
          <p className="text-sm text-[#5B616E] mt-1">Real-time event coverage</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-live-blog-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Live Blog</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
      ) : blogs.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {blogs.map(b => (
            <Card key={b.id} className="bg-white border-[#E7E0D6] hover:shadow-md transition-shadow cursor-pointer" onClick={() => openDetail(b.id)} data-testid="live-blog-card">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      {b.is_active && <span className="w-2 h-2 rounded-full bg-[#B3261E] animate-pulse" />}
                      <Badge variant={b.is_active ? 'default' : 'outline'} className={b.is_active ? 'bg-[#B3261E] text-white text-[10px]' : 'text-[10px]'}>{b.is_active ? 'LIVE' : 'Ended'}</Badge>
                    </div>
                    <h3 className="font-medium mt-1">{b.title}</h3>
                    <p className="text-xs text-[#5B616E] mt-1">{b.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[#E7E0D6] text-xs text-[#5B616E]">
                  <span>{b.update_count || 0} updates</span>
                  <span>{b.created_at ? new Date(b.created_at).toLocaleDateString() : ''}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]">
          <Radio className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No live blogs yet</p>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create Live Blog</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Title</label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="live-blog-title-input" /></div>
            <div><label className="text-sm font-medium mb-1 block">Description</label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createBlog} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{detailData?.title || 'Live Blog'}</DialogTitle>
              <Button variant="ghost" size="sm" className="text-[#B3261E]" onClick={() => deleteBlog(showDetail)}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </DialogHeader>
          <div className="space-y-4">
            {/* Actions bar */}
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="text-xs" onClick={() => summarizeBlog(showDetail)}>✨ AI Summary</Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => convertToArticle(showDetail)}>→ Convert to Article</Button>
            </div>

            {/* Post Update */}
            <div className="space-y-2">
              <div className="flex gap-2">
                {['text','image','video','quote','highlight'].map(t => (
                  <button key={t} onClick={() => setUpdateType(t)}
                    className={`px-2 py-1 rounded text-[10px] font-medium border transition-colors ${updateType === t ? 'bg-[#121316] text-white border-[#121316]' : 'border-[#E7E0D6] text-[#5B616E]'}`}>
                    {t}
                  </button>
                ))}
              </div>
              {(updateType === 'image' || updateType === 'video') && (
                <input value={updateMedia} onChange={e => setUpdateMedia(e.target.value)} placeholder={`${updateType} URL...`} className="w-full border border-[#E7E0D6] rounded-lg px-3 py-1.5 text-xs bg-white" />
              )}
              <div className="flex gap-2">
                <Textarea value={updateText} onChange={e => setUpdateText(e.target.value)} placeholder="Post a live update..." className="flex-1" data-testid="live-update-input" />
                <Button onClick={postUpdate} className="bg-[#121316] text-white hover:bg-[#1B1D22] self-end"><Send className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* Pinned updates first */}
            {(detailData?.updates || []).filter(u => u.is_pinned).length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-[10px] font-semibold text-amber-700 mb-2 flex items-center gap-1"><Pin className="w-3 h-3" /> PINNED KEY MOMENTS</p>
                {(detailData?.updates || []).filter(u => u.is_pinned).map((u, i) => (
                  <div key={i} className="flex items-start gap-2 mb-1">
                    <p className="text-xs flex-1">{u.content}</p>
                    <button onClick={() => togglePin(showDetail, u.id, true)} className="text-amber-600 hover:text-amber-800"><PinOff className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Updates Timeline */}
            <div className="space-y-3">
              {(detailData?.updates || []).filter(u => !u.is_pinned).map((u, i) => (
                <div key={u.id || i} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-2 h-2 rounded-full bg-[#B89B5E] mt-2" />
                    {i < (detailData?.updates?.length || 0) - 1 && <div className="w-px flex-1 bg-[#E7E0D6]" />}
                  </div>
                  <div className="flex-1 pb-4">
                    {u.update_type === 'image' && u.metadata?.media_url && <img src={u.metadata.media_url} alt="" className="w-full rounded-lg mb-2 max-h-48 object-cover" />}
                    {u.update_type === 'video' && u.metadata?.media_url && <video src={u.metadata.media_url} controls className="w-full rounded-lg mb-2 max-h-48" />}
                    <p className={`text-sm ${u.update_type === 'quote' ? 'italic border-l-2 border-[#B89B5E] pl-2' : ''} ${u.update_type === 'highlight' ? 'font-semibold bg-yellow-50 px-2 py-1 rounded' : ''}`}>{u.content}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-[#5B616E]">
                      <Clock className="w-3 h-3" />
                      <span>{u.created_at ? new Date(u.created_at).toLocaleString() : ''}</span>
                      {u.author_name && <span>by {u.author_name}</span>}
                      {u.update_type !== 'text' && <span className="bg-[#E7E0D6] rounded px-1">{u.update_type}</span>}
                    </div>
                    <button onClick={() => togglePin(showDetail, u.id, false)} className="text-[10px] text-[#5B616E] hover:text-[#B89B5E] flex items-center gap-0.5 mt-1"><Pin className="w-2.5 h-2.5" /> Pin as key moment</button>
                  </div>
                </div>
              ))}
              {(!detailData?.updates || detailData.updates.length === 0) && <p className="text-sm text-[#5B616E] text-center py-4">No updates yet</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
